<template>
  <div class="page">
    <div class="row text-header">
      <div class="columns">
        <h1>We're Here to Help</h1>
      </div>
      <div class="columns medium-10 medium-offset-1 end text-center">
        <h2>
          Our member services team is ready to help via phone or email. Call us
          at <CustomerServicePhoneLink :with-hours-long="true" /> or send us an
          email at <CustomerServiceEmailLink />
        </h2>
      </div>
    </div>

    <div class="row faqs">
      <div
        class="columns medium-4 medium-push-8 large-3 large-offset-1"
        style="padding-bottom: 4rem"
      >
        <h4>Manage Your Account</h4>
        <ul class="links">
          <li>
            <nuxt-link to="/accounts/orders/"
              >Update your delivery date</nuxt-link
            >
          </li>
          <li>
            <nuxt-link to="/accounts/addresses/"
              >Manage your shipping addresses</nuxt-link
            >
          </li>
          <li>
            <nuxt-link to="/accounts/orders/">Add wines to an order</nuxt-link>
          </li>
          <li>
            <nuxt-link to="/accounts/profile/">Update your profile</nuxt-link>
          </li>
          <li>
            <nuxt-link to="/accounts/change-password/"
              >Change your password</nuxt-link
            >
          </li>
          <li>
            <nuxt-link to="/accounts/profile/"
              >Change email preferences</nuxt-link
            >
          </li>
          <li>
            <nuxt-link to="/cellar/wines/">View your wine cellar</nuxt-link>
          </li>
          <li>
            <nuxt-link to="/accounts/payment_methods/"
              >Manage your credit cards</nuxt-link
            >
          </li>
        </ul>

        <h4>Privacy &amp; Security</h4>
        <ul class="links">
          <li><a href="/privacy-policy/">Privacy Policy</a></li>
          <li><a href="/terms/">Terms of Use</a></li>
        </ul>

        <h4>Mailing Address</h4>
        <address>
          WineAccess, Inc.<br />
          122 Camino Oruga<br />
          Building A<br />
          Napa, CA 94558 <br />
        </address>
      </div>
      <div class="columns medium-8 medium-pull-4">
        <h2>FAQs</h2>

        <h3>About Wine Access</h3>

        <div data-multi-accordion>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="about-1"
              >What is Wine Access?</a
            >
            <div id="about-1" class="accordion-content">
              <p>
                We are wine lovers and searchers. Since 2007, each day, we've
                written stories about wines we've discovered, tasted and
                evaluated in the cellars where they were made. About 1 out of 18
                make the cut. We negotiate prices on our membership's behalf,
                then arrange to have bottles shipped directly from the source
                &mdash; right to your door.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="about-2"
              >How are the wines chosen?</a
            >
            <div id="about-2" class="accordion-content">
              <p>
                Great wine is all about one man's or woman's expression of her
                land in a given growing season. We don't simply present wine
                offers. We research our selections, helping you understand why
                each wine is special. It's often in the 'why' that one makes
                sense of what one tastes.
              </p>
              <p>
                Once we've done the research, talking to the winemakers and
                identifying promising wines, we taste each bottle ourselves and
                pick only the best ones to feature. And it's often not about
                high scores from the well-known experts. Our experience is that
                "highly scored" wines aren't always what they're cracked up to
                be, or are often not good values. If you rely solely on scores,
                you will miss many wonderful discoveries.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="about-3"
              >How do I find out about your offers?</a
            >
            <div id="about-3" class="accordion-content">
              <p>
                Wine Access offers delicious wines that hail from the most
                well-known wine regions in the world. We have offerings that
                range from red wine, white wine, dessert wine, rose wine,
                sparkling wine and much more. For our Discoveries, email is the
                best way to purchase offers before they sell out (which they
                usually do). Just
                <nuxt-link to="/accounts/profile/">sign up here </nuxt-link>to
                start receiving email offers from us.
              </p>
            </div>
          </div>
        </div>

        <h3>Shipping</h3>
        <p v-if="passageOfTitleStatement">
          Please call at
          <CustomerServicePhoneLink :with-hours-long="true" /> if you'd like to
          arrange your own shipment or pickup option.
        </p>
        <div data-multi-accordion>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-1"
              >Do you ship to my state?</a
            >
            <div id="shipping-1" class="accordion-content">
              <p>
                Shipping laws vary from state to state.
                <a href="/where-we-ship/">This map</a> shows the
                <a href="/where-we-ship/">states we ship to</a>. We'll only show
                you wines that are available in the state you've indicated you
                are shipping to. If you change your shipping address during
                checkout, we'll let you know if any of the wines in your cart
                cannot be shipped to your location.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-2"
              >Does Wine Access ship outside the United States?</a
            >
            <div id="shipping-2" class="accordion-content">
              <p>Nope.</p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-3"
              >How much does shipping cost?</a
            >
            <div id="shipping-3" class="accordion-content">
              <p>
                Ground shipping is included when you spend ${{
                  metadata.settingsVars.STORE_FREE_SHIPPING_DOLLARS
                }}
                or more—otherwise, it varies by location and shipping method.
                <span v-if="metadata.settingsVars.ICE_PACK_SURCHARGE"
                  >{{ metadata.constance.TCP_LONGNAME }} is available at
                  checkout for ${{ metadata.settingsVars.ICE_PACK_SURCHARGE }},
                  or complimentary when you spend ${{
                    metadata.settingsVars.FREE_ICE_PACK_DOLLARS
                  }}
                  or more.
                </span>
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-4"
              >How long will it take before my wine is delivered?</a
            >
            <div id="shipping-4" class="accordion-content">
              <p>
                At checkout, you have the ability to select a delivery date.
                Most orders can be shipped from two to 14 days after they are
                placed. We know patience is difficult when delicious wine is on
                the way, but our careful shipping process guarantees that your
                wines will arrive in pristine condition.
              </p>
              <p>
                In order to avoid hot shipping conditions over the summer, we
                can hold your wine in our climate-controlled warehouse until
                temperatures permit safe shipping. Some "pre-arrival"
                offers&mdash;largely collectibles&mdash;are shipped directly
                from the cellars and can take as long as three months to arrive
                at your door.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-5"
              >Why should I have my Wine Access shipment sent to a business
              address?</a
            >
            <div id="shipping-5" class="accordion-content">
              <p>
                <strong
                  >Federal law requires that someone of at least 21 years of age
                  sign for all wine shipments.</strong
                >
                If no adult is home at the time of delivery, FedEx or UPS is
                obliged to return the wine to its local warehouse, and then
                reschedule delivery.
              </p>
              <p>
                If you have your Wine Access shipment sent to a business
                address, delivery usually takes place in the mid-morning and can
                be signed for by any adult on the premises.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-6"
              >Does an adult have to sign for my shipment?</a
            >
            <div id="shipping-6" class="accordion-content">
              <p>
                Yes, by law, a person who is at least 21 years old must sign for
                the delivery, without exception.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-7"
              >How does Wine Access take care of my wine during shipping?</a
            >
            <div id="shipping-7" class="accordion-content">
              <p>
                Getting bottles from the winery to our members’ doors in top
                condition is our #1 priority, so we take shipping very
                seriously. We work to protect your wine from light and
                temperature fluctuations, both of which can damage a wine’s
                aromas and flavors, from the moment it leaves the winery, and we
                control every step of the process to ensure that the wine in the
                bottle is the wine on the label.
              </p>
              <p>
                The shipping process from our climate-controlled warehouse
                involves zip-code-by-zip-code shipping to ensure wines do not
                travel through overly hot or cold environments; adjustable
                delivery dates to make sure you’ll be home when your wine
                arrives; {{ metadata.constance.TCP_LONGNAME }} available during
                extreme temperatures; and more, all with the goal of protecting
                your wine.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="shipping-8"
              >What happens if I have a shipment that is damaged in transit?</a
            >
            <div id="shipping-8" class="accordion-content">
              <p>
                If you receive items that have been damaged during transit,
                please contact Member Services within 7 days from the delivery
                date and include an image and description of the damage if
                possible. We will replace the damaged products if we have
                additional stock on hand; otherwise, we will issue a full refund
                or store credit—whichever you’d prefer.
              </p>
            </div>
          </div>
        </div>

        <h3>Ordering</h3>

        <div data-multi-accordion>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="ordering-1"
              >How do I check the status of an order?</a
            >
            <div id="ordering-1" class="accordion-content">
              <p>
                Sign into <a href="/">www.wineaccess.com</a>. Click on your name
                in the site navigation and select
                <nuxt-link to="/accounts/orders/">"Orders"</nuxt-link>.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="ordering-2"
              >Can I cancel an order after I've placed it?</a
            >
            <div id="ordering-2" class="accordion-content">
              <p>
                Absolutely, as long as it hasn't already been shipped. Simply
                email Member Services at <CustomerServiceEmailLink />. Or call
                <CustomerServicePhoneLink :with-hours-long="true" />.
              </p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="ordering-3"
              >Does Wine Access accept returns?</a
            >
            <div id="ordering-3" class="accordion-content">
              <p>If any wine fails to impress, we'll credit that bottle.</p>
            </div>
          </div>
          <div class="accordion-navigation">
            <a class="accordion-toggle" data-target="ordering-4"
              >How do I see what wines I've bought from you before?</a
            >
            <div id="ordering-4" class="accordion-content">
              <p>
                Sign into <a href="/">www.wineaccess.com</a>. Click on your name
                in the site navigation and select
                <nuxt-link to="/cellar/wines/">My Cellar</nuxt-link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'ContactUs',
  computed: {
    ...mapGetters(['cart']),
    passageOfTitleStatement() {
      return this.cart.passage_of_title_statement
    },
  },
  created() {
    this.metadata = useMetadata()
  },
  mounted() {
    this.$store.dispatch('getCart')
  },
}
</script>
